import React, { useMemo } from 'react';
import { Button, Tooltip, Select, Row, Col, Divider } from 'antd';
import { FieldArray, change, getFormValues } from 'redux-form';
import { Individuals, Corporation } from '..';
import { FormField, InputTypes, getOptions } from 'common';
import {
  BANKRUPTCY_FORM,
  BankruptcyFormLabels,
  BankruptcyFormPlaceholders,
  TAB_INDEX,
  EMPTY_VALUE,
  FormNames,
  FieldGroupNames,
  BANKRUPTCY_FORM_INITIAL_ITEM_GROUP,
} from '../../Bankruptcy.constants';
import {
  getBankruptcyStatesOptions,
  bankruptcyCountyOptionsSelector,
  bankruptcyDistrictSelector,
  getCounties,
  getCurrentSectionNo,
  getCurrentSection,
  getIsBankruptcySelected,
  requiredWarn,
} from '../../Bankruptcy.selectors';
import styles from '../../Bankruptcy.module.scss';
import { useDispatch, useSelector } from 'react-redux';
import { AppState } from 'store';
import { createSelector } from 'reselect';

const ButtonGroup = Button.Group;
const { Option } = Select;
const addLimit = 2;
const removeLimit = 1;
const selector = createSelector(
  (state: AppState) => bankruptcyCountyOptionsSelector(state),
  (state: AppState) => bankruptcyDistrictSelector(state),
  (state: AppState) => getFormValues(BANKRUPTCY_FORM)(state),
  (getBankruptcyCountyOptions, getBankruptcyDistrict, formValues: any) => ({
    getBankruptcyCountyOptions,
    getBankruptcyDistrict,
    formValues,
  }),
);

const BankruptcyItemsGroup: React.FC = ({ bankruptcyStates, fields }: any) => {
  const dispatch = useDispatch();

  const { getBankruptcyCountyOptions, getBankruptcyDistrict, formValues } =
    useSelector(selector);

  const clearField = React.useCallback(
    (fieldName: string) => {
      dispatch(change(BANKRUPTCY_FORM, fieldName, EMPTY_VALUE));
    },
    [dispatch],
  );

  const handleStateChange = React.useCallback(
    (field: any) => {
      clearField(`${field}.${FormNames.COUNTY}`);
      clearField(`${field}.${FormNames.DISTRICT}`);
    },
    [clearField],
  );

  const handleCountyChange = React.useCallback(
    (field: string, districtCode: string) => {
      const fieldName = `${field}.${FormNames.DISTRICT}`;
      dispatch(change(BANKRUPTCY_FORM, fieldName, districtCode));
    },
    [dispatch],
  );

  const statesOptions = useMemo(
    () => getBankruptcyStatesOptions(bankruptcyStates),
    [bankruptcyStates],
  );

  const requiredState = React.useCallback(
    (stateValue, formValues, _, fieldName) => {
      const currentSectionNo = getCurrentSectionNo(fieldName);
      const currentSection = getCurrentSection(formValues, currentSectionNo);
      const isBankruptcySelected =
        currentSection &&
        currentSection.individuals &&
        currentSection.corporations &&
        getIsBankruptcySelected(currentSection);
      if (stateValue) return undefined;
      if (isBankruptcySelected) return requiredWarn;
      return undefined;
    },
    [],
  );

  const requiredCounty = React.useCallback(
    (countyValue, formValues, _, fieldName) => {
      const currentSectionNo = getCurrentSectionNo(fieldName);
      const currentSection = getCurrentSection(formValues, currentSectionNo);
      const stateHob = currentSection?.hobCodes.stateHob;
      const counties = stateHob
        ? getCounties(bankruptcyStates, stateHob)
        : null;
      const isBankruptcySelected =
        currentSection &&
        currentSection.individuals &&
        currentSection.corporations &&
        getIsBankruptcySelected(currentSection);
      if (countyValue) return undefined;
      if (isBankruptcySelected) {
        return stateHob && counties?.length ? requiredWarn : undefined;
      }
      return undefined;
    },
    [bankruptcyStates],
  );

  return (
    <Row type="flex">
      <Col>
        {fields.map((field: any, index: number) => {
          const stateHob =
            formValues.items[index] &&
            formValues.items[index].hobCodes.stateHob;
          const countyHob =
            formValues.items[index] &&
            formValues.items[index].hobCodes.сountyHob;
          const district = getBankruptcyDistrict(stateHob, countyHob);
          const countyOptions = getBankruptcyCountyOptions(stateHob);
          return (
            <React.Fragment key={index}>
              <Row type="flex">
                <FormField
                  validate={[requiredState]}
                  tabIndex={TAB_INDEX}
                  label={BankruptcyFormLabels.STATE}
                  name={`${field}.${FormNames.STATE}`}
                  placeholder={BankruptcyFormPlaceholders.STATE}
                  inputType={InputTypes.SELECT}
                  className={`${styles.select} ${styles.input}`}
                  allowClear={false}
                  onChange={() => handleStateChange(field)}
                  showSearch={true}
                  filterOption={(input: any, option: any) =>
                    option.props.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {getOptions(Option, statesOptions)}
                </FormField>
                <FormField
                  validate={[requiredCounty]}
                  disabled={countyOptions.length < 2}
                  tabIndex={TAB_INDEX}
                  label={BankruptcyFormLabels.COUNTY}
                  name={`${field}.${FormNames.COUNTY}`}
                  placeholder={BankruptcyFormPlaceholders.COUNTY}
                  inputType={InputTypes.SELECT}
                  className={`${styles.select} ${styles.input}`}
                  allowClear={false}
                  onChange={(value: string) => {
                    const dstrct = getBankruptcyDistrict(stateHob, value);
                    const districtCode = dstrct
                      ? dstrct.districtCode
                      : EMPTY_VALUE;
                    return handleCountyChange(field, districtCode);
                  }}
                  showSearch={true}
                  filterOption={(input: any, option: any) =>
                    option.props.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {getOptions(Option, countyOptions)}
                </FormField>
                {district && (
                  <FormField
                    label="District"
                    name={`${field}.districtName`}
                    disabled={true}
                    placeholder={district.name}
                  />
                )}
              </Row>
              <FieldArray
                name={`${field}.${FieldGroupNames.INDIVIDUALS}`}
                component={Individuals}
              />
              <FieldArray
                name={`${field}.${FieldGroupNames.CORPORATIONS}`}
                component={Corporation}
              />
              <ButtonGroup>
                <Tooltip title={`Remove Name`}>
                  <Button
                    type="danger"
                    size="small"
                    icon="minus"
                    onClick={() => fields.remove(index)}
                    disabled={fields.length === removeLimit}
                  >
                    Remove
                  </Button>
                </Tooltip>
                {fields.length - 1 === index && (
                  <Tooltip title={`Add Name (the limit is ${addLimit})`}>
                    <Button
                      type="primary"
                      size="small"
                      icon="plus"
                      onClick={() =>
                        fields.push(BANKRUPTCY_FORM_INITIAL_ITEM_GROUP)
                      }
                      disabled={fields.length >= addLimit}
                    >
                      Add
                    </Button>
                  </Tooltip>
                )}
              </ButtonGroup>
              <Divider dashed />
            </React.Fragment>
          );
        })}
      </Col>
    </Row>
  );
};

export default BankruptcyItemsGroup;
